import {
  ActionSheet,
  ActionSheetContent,
  ActionSheetItem,
  ActionSheetTrigger,
} from '@mdvip/shared/components/blocks/ActionSheet';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { NetworkChip } from '@mdvip/shared/components/blocks/NetworkChip';
import { Badge } from '@mdvip/shared/components/ui/badge';
import { Button } from '@mdvip/shared/components/ui/button';
import { Card } from '@mdvip/shared/components/ui/card';
import { useTranslation } from 'react-i18next';
import type { BankAccountAccount } from '@/lib/payments/bankAccountAccount.types';
import { formatCityStateZip } from '@/lib/payments/paymentAccount.format';

interface BankAccountRowProps {
  account: BankAccountAccount;
  onSetDefault: (id: string) => void;
  isSettingDefault?: boolean;
  onEdit: (id: string) => void;
  onRemove: (account: BankAccountAccount) => void;
  /** True when the account has a scheduled one-time payment — Remove is hidden, same as the default guard. */
  hasUpcomingPayment?: boolean;
}

export function BankAccountRow({
  account,
  onSetDefault,
  isSettingDefault = false,
  onEdit,
  onRemove,
  hasUpcomingPayment = false,
}: BankAccountRowProps) {
  const { t } = useTranslation('members');
  const canRemove = !account.isDefault && !hasUpcomingPayment;

  const number = `···· ${account.last6}`;
  const accountTypeLabel = t(`payments.pages.paymentAccounts.bankAccounts.accountType.${account.accountType}`);
  const typeLine = `${account.bankName} · ${accountTypeLabel}`;
  const cityStateZip = formatCityStateZip(account.city, account.state, account.zip);
  const holderAddressLine = [account.holderName, cityStateZip].filter(Boolean).join(' · ');

  return (
    <Card className="flex-row! items-center gap-1! rounded-xl border border-border py-0! pr-2 shadow-sm">
      <div className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3.5">
        <NetworkChip kind="bank" />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="truncate text-sm font-semibold text-foreground">{number}</span>
            {account.isDefault && (
              <Badge variant="success">
                <Icon name="circleCheck" size="xs" />
                {t('payments.pages.paymentAccounts.bankAccounts.defaultBadge')}
              </Badge>
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{typeLine}</p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground sm:hidden">{holderAddressLine}</p>
        </div>

        <div className="hidden max-w-[44%] shrink-0 flex-col items-end pl-4 text-right sm:flex">
          <span className="truncate text-sm font-medium text-foreground">{account.holderName}</span>
          <span className="truncate text-xs text-muted-foreground">{cityStateZip}</span>
        </div>
      </div>

      <ActionSheet>
        <ActionSheetTrigger
          render={
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('payments.pages.paymentAccounts.bankAccounts.rowActionsLabel', {
                last6: account.last6,
              })}
              className="shrink-0 rounded-full text-muted-foreground"
            />
          }
        >
          <Icon name="ellipsisVertical" />
        </ActionSheetTrigger>
        <ActionSheetContent
          title={number}
          align="end"
          note={
            account.isDefault
              ? t('payments.pages.paymentAccounts.bankAccounts.defaultCannotRemove')
              : hasUpcomingPayment
                ? t('payments.pages.paymentAccounts.bankAccounts.upcomingPaymentCannotRemove')
                : undefined
          }
        >
          {!account.isDefault && (
            <ActionSheetItem
              icon={<Icon name="circleCheck" size="sm" />}
              onSelect={() => onSetDefault(account.id)}
              disabled={isSettingDefault}
            >
              {t('payments.pages.paymentAccounts.bankAccounts.setAsDefault')}
            </ActionSheetItem>
          )}
          <ActionSheetItem icon={<Icon name="pencil" size="sm" />} onSelect={() => onEdit(account.id)}>
            {t('payments.pages.paymentAccounts.bankAccounts.editDetails')}
          </ActionSheetItem>
          {canRemove && (
            <ActionSheetItem icon={<Icon name="trash" size="sm" />} onSelect={() => onRemove(account)} destructive>
              {t('payments.pages.paymentAccounts.bankAccounts.removeAccount')}
            </ActionSheetItem>
          )}
        </ActionSheetContent>
      </ActionSheet>
    </Card>
  );
}
